import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useSessionMetricsFilters } from '@va/dashboard/shared/filters';
import { useReportBlockNodeBuilder } from '@va/dashboard/shared/report-block-nodes';
import { useLocalizationContext, useTranslate } from '@va/localization';
import { AppliedFilters, FiltersContextProvider, useFiltersContext } from '@va/shared/feature-filters';
import { DataViewOption, ReportBlockNodes, ReportBlockProps } from '@va/types/report-block';
import { SortingDirection } from '@va/types/requests';
import { useControlledTableState } from '@va/ui/components/data-table';
import { PaginationV8 } from '@va/ui/components/data-table/PaginationV8';
import { ProgressListCardContent } from '@va/ui/components/progress-list-card';
import { ReportBlock, ReportBlockNodeIds, useReportBlockCtx } from '@va/ui/components/report-block';
import { IconWithBackground } from '@va/ui/design-system';
import { Flag, useRefreshCallbacks } from '@va/util/components';
import { addNumberSeparator } from '@va/util/helpers';
import { memo, useMemo } from 'react';
import { useSessionsByCity } from './apiClient';

const REPORT_BLOCK_ID = 'visitors-by-city';

const VisitorsByCityCard = () => {
  const translate = useTranslate();
  const { allFilters } = useSessionMetricsFilters();
  const buildReportBlockNode = useReportBlockNodeBuilder();

  const nodes = useMemo<ReportBlockNodes>(
    () => ({
      filterButton: buildReportBlockNode(ReportBlockNodeIds.FILTER_BUTTON),
      appliedFilters: <AppliedFilters />,
    }),
    [buildReportBlockNode],
  );

  const visualizationComponents = useMemo<ReportBlockProps['visualizationComponents']>(
    () => ({ [DataViewOption.horizontalBarChart]: HorizontalBarChart }),
    [],
  );

  return (
    <FiltersContextProvider reportBlockId={REPORT_BLOCK_ID} allFilterOptions={allFilters}>
      <ReportBlock
        id={REPORT_BLOCK_ID}
        visualization={{ selectedView: DataViewOption.horizontalBarChart }}
        visualizationComponents={visualizationComponents}
        title={translate('all.latestVisitors.locations.byCity.title')}
        nodes={nodes}
      />
    </FiltersContextProvider>
  );
};

export default memo(VisitorsByCityCard);

const HorizontalBarChart = () => {
  const { translate, locale } = useLocalizationContext();
  const { paginationId } = useReportBlockCtx();
  const { createApiConditions } = useFiltersContext();
  const { pagination, setPagination } = useControlledTableState(
    { pagination: { pageIndex: 0, pageSize: 6 } },
    REPORT_BLOCK_ID,
  );

  const { data, isLoading, mutate } = useSessionsByCity({
    pagination: { page: pagination.pageNumber, pageSize: pagination.pageSize },
    order: [{ member: 'sessionPercentOfTotal', direction: SortingDirection.Desc }],
    where: { and: createApiConditions() },
  });

  const progressListItems = useMemo(
    () =>
      data?.payload.map(({ countryCode, cityName, sessionCount, sessionPercentOfTotal }) => {
        return {
          count: sessionCount,
          countLabel: translate('sessionsCount', { count: addNumberSeparator(sessionCount, locale) }),
          percentage: sessionPercentOfTotal,
          title: cityName || translate('labels.unknown'),
          progress: [{ color: window.PRIMARY_COLOR, percentage: sessionPercentOfTotal }],
          icon: (
            <IconWithBackground
              className='bg-white-snow group-hover:bg-white'
              icon={() => <Flag countryCode={countryCode} size='1x1' className='w-18 h-18 rounded-full' />}
            />
          ),
        };
      }) ?? [],
    [data, locale, translate],
  );

  const table = useReactTable({
    columns: [],
    data: [],
    getCoreRowModel: getCoreRowModel(),
    state: { pagination },
    onPaginationChange: setPagination,
    rowCount: data?.meta.total,
    manualPagination: true,
  });

  const { isManualRefreshing } = useRefreshCallbacks(mutate);

  return (
    <>
      <PaginationV8 table={table} containerId={paginationId} />
      <ProgressListCardContent
        items={progressListItems}
        isLoading={isLoading || isManualRefreshing}
        placeholdersCount={pagination.pageSize}
      />
    </>
  );
};
